/**
 * MAP-056. Natural-language dates, resolved as civil dates in a named zone.
 *
 * Everything here works on YYYY-MM-DD strings and plain day arithmetic, never
 * on instants, so DST transitions cannot shift a due date by a day. "Today"
 * means today where the user is, which is the only definition that matches what
 * they mean when they type "fri".
 */
const MS_PER_DAY = 86_400_000;
export function todayIn(timeZone, now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    return { year: get('year'), month: get('month'), day: get('day') };
}
export function toIso(d) {
    return `${String(d.year).padStart(4, '0')}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
}
export function fromIso(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return { year: y ?? 1970, month: m ?? 1, day: d ?? 1 };
}
/** Days are counted on a UTC clock purely as arithmetic; no zone is implied. */
function toEpochDay(d) {
    return Date.UTC(d.year, d.month - 1, d.day) / MS_PER_DAY;
}
function fromEpochDay(day) {
    const date = new Date(day * MS_PER_DAY);
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}
/** How far the zone is from UTC at a given instant, in milliseconds. */
function zoneOffsetMs(at, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(at);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return wall - at.getTime();
}
/**
 * The last instant of a civil day where the user lives.
 *
 * A day looked back at has to end where the user's day ended, not at UTC
 * midnight: otherwise an evening's work in London lands in tomorrow's
 * snapshot in winter and today's in summer. The offset is resolved twice
 * because the first answer is taken at the wrong instant when the clocks
 * change that night.
 */
export function endOfCivilDay(iso, timeZone) {
    const next = addDays(fromIso(iso), 1);
    const wall = Date.UTC(next.year, next.month - 1, next.day);
    let midnight = new Date(wall - zoneOffsetMs(new Date(wall), timeZone));
    midnight = new Date(wall - zoneOffsetMs(midnight, timeZone));
    return new Date(midnight.getTime() - 1);
}
export function addDays(d, n) {
    return fromEpochDay(toEpochDay(d) + n);
}
export function addMonths(d, n) {
    const total = d.year * 12 + (d.month - 1) + n;
    const year = Math.floor(total / 12);
    const month = (total % 12) + 1;
    const lastDay = daysInMonth(year, month);
    return { year, month, day: Math.min(d.day, lastDay) };
}
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** 0 = Sunday … 6 = Saturday. */
export function weekdayOf(d) {
    return new Date(Date.UTC(d.year, d.month - 1, d.day)).getUTCDay();
}
const WEEKDAYS = {
    sunday: 0, sun: 0,
    monday: 1, mon: 1,
    tuesday: 2, tue: 2, tues: 2,
    wednesday: 3, wed: 3, weds: 3,
    thursday: 4, thu: 4, thur: 4, thurs: 4,
    friday: 5, fri: 5,
    saturday: 6, sat: 6,
};
const MONTHS = {
    january: 1, jan: 1,
    february: 2, feb: 2,
    march: 3, mar: 3,
    april: 4, apr: 4,
    may: 5,
    june: 6, jun: 6,
    july: 7, jul: 7,
    august: 8, aug: 8,
    september: 9, sep: 9, sept: 9,
    october: 10, oct: 10,
    november: 11, nov: 11,
    december: 12, dec: 12,
};
const UNIT_DAYS = { day: 1, days: 1, week: 7, weeks: 7 };
/** The next occurrence of a weekday, counting today as a match. */
function nextWeekday(from, target, skipThisWeek = false) {
    const current = weekdayOf(from);
    let delta = (target - current + 7) % 7;
    if (skipThisWeek)
        delta = delta === 0 ? 7 : delta + 7;
    return addDays(from, delta);
}
const RULES = [
    { pattern: /\b(today|tonight)\b/i, resolve: (_m, t) => t },
    { pattern: /\b(tomorrow|tmw|tmr)\b/i, resolve: (_m, t) => addDays(t, 1) },
    { pattern: /\byesterday\b/i, resolve: (_m, t) => addDays(t, -1) },
    // "in 3 weeks", "in 10 days"
    {
        pattern: /\bin\s+(\d{1,3})\s*(day|days|week|weeks)\b/i,
        resolve: (m, t) => addDays(t, Number(m[1]) * (UNIT_DAYS[(m[2] ?? '').toLowerCase()] ?? 1)),
    },
    {
        pattern: /\bin\s+(\d{1,3})\s*(month|months)\b/i,
        resolve: (m, t) => addMonths(t, Number(m[1])),
    },
    {
        pattern: /\bin\s+(\d{1,2})\s*(year|years)\b/i,
        resolve: (m, t) => ({ ...t, year: t.year + Number(m[1]) }),
    },
    // "end of month", "end of the week"
    {
        pattern: /\bend of (?:the )?month\b|\beom\b/i,
        resolve: (_m, t) => ({ ...t, day: daysInMonth(t.year, t.month) }),
    },
    {
        pattern: /\bstart of (?:the )?month\b/i,
        resolve: (_m, t) => addMonths({ ...t, day: 1 }, 1),
    },
    {
        // End of the working week.
        pattern: /\bend of (?:the )?week\b|\beow\b/i,
        resolve: (_m, t) => nextWeekday(t, 5),
    },
    { pattern: /\bnext week\b/i, resolve: (_m, t) => addDays(t, 7) },
    { pattern: /\bnext month\b/i, resolve: (_m, t) => addMonths(t, 1) },
    { pattern: /\bnext year\b/i, resolve: (_m, t) => ({ ...t, year: t.year + 1 }) },
    // "next friday" (the one after this coming friday) vs "friday"/"this friday"
    {
        pattern: /\bnext\s+(sunday|sun|monday|mon|tuesday|tues|tue|wednesday|weds|wed|thursday|thurs|thur|thu|friday|fri|saturday|sat)\b/i,
        resolve: (m, t) => {
            const target = WEEKDAYS[(m[1] ?? '').toLowerCase()];
            return target === undefined ? null : nextWeekday(t, target, true);
        },
    },
    {
        pattern: /\b(?:this\s+|on\s+)?(sunday|sun|monday|mon|tuesday|tues|tue|wednesday|weds|wed|thursday|thurs|thur|thu|friday|fri|saturday|sat)\b/i,
        resolve: (m, t) => {
            const target = WEEKDAYS[(m[1] ?? '').toLowerCase()];
            return target === undefined ? null : nextWeekday(t, target);
        },
    },
    // ISO
    {
        pattern: /\b(\d{4})-(\d{2})-(\d{2})\b/,
        resolve: (m) => {
            const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
            if (mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo))
                return null;
            return { year: y, month: mo, day: d };
        },
    },
    // "15 march", "15th march 2027"
    {
        pattern: /\b(\d{1,2})(?:st|nd|rd|th)?\s+(january|jan|february|feb|march|mar|april|apr|may|june|jun|july|jul|august|aug|september|sept|sep|october|oct|november|nov|december|dec)\.?(?:\s+(\d{4}))?\b/i,
        resolve: (m, t) => {
            const day = Number(m[1]);
            const month = MONTHS[(m[2] ?? '').toLowerCase()];
            if (month === undefined)
                return null;
            const year = m[3] ? Number(m[3]) : rollForward(t, month, day);
            if (day < 1 || day > daysInMonth(year, month))
                return null;
            return { year, month, day };
        },
    },
    // "march 15"
    {
        pattern: /\b(january|jan|february|feb|march|mar|april|apr|may|june|jun|july|jul|august|aug|september|sept|sep|october|oct|november|nov|december|dec)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:\s+(\d{4}))?\b/i,
        resolve: (m, t) => {
            const month = MONTHS[(m[1] ?? '').toLowerCase()];
            const day = Number(m[2]);
            if (month === undefined)
                return null;
            const year = m[3] ? Number(m[3]) : rollForward(t, month, day);
            if (day < 1 || day > daysInMonth(year, month))
                return null;
            return { year, month, day };
        },
    },
    // UK day/month order: 15/3, 15/03/2027, 15-3-27
    {
        pattern: /\b(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?\b/,
        resolve: (m, t) => {
            const day = Number(m[1]);
            const month = Number(m[2]);
            if (month < 1 || month > 12 || day < 1)
                return null;
            let year;
            if (m[3] === undefined)
                year = rollForward(t, month, day);
            else if (m[3].length === 2)
                year = 2000 + Number(m[3]);
            else
                year = Number(m[3]);
            if (day > daysInMonth(year, month))
                return null;
            return { year, month, day };
        },
    },
];
/** A bare day and month means the next time that date comes round. */
function rollForward(today, month, day) {
    const thisYear = today.year;
    const candidate = toEpochDay({ year: thisYear, month, day });
    return candidate >= toEpochDay(today) ? thisYear : thisYear + 1;
}
/**
 * Finds the last date phrase in the text. Returns null rather than guessing:
 * a wrong date is far worse than no date.
 */
export function parseDatePhrase(text, options = {}) {
    const today = options.today ?? todayIn(options.timeZone ?? 'Europe/London', options.now ?? new Date());
    const candidates = [];
    for (const rule of RULES) {
        const global = new RegExp(rule.pattern.source, `${rule.pattern.flags.replace('g', '')}g`);
        for (const match of text.matchAll(global)) {
            if (match.index === undefined)
                continue;
            const resolved = rule.resolve(match, today);
            if (!resolved)
                continue;
            candidates.push({
                date: toIso(resolved),
                start: match.index,
                end: match.index + match[0].length,
                text: match[0],
            });
        }
    }
    // "next tuesday" also matches the bare "tuesday" rule; the longer phrase is
    // the one the user meant, so anything wholly inside another match is dropped.
    const outermost = candidates.filter((c) => !candidates.some((other) => other !== c && other.start <= c.start && other.end >= c.end));
    // Of what is left, the last phrase wins: "moved from today to next friday".
    return outermost.reduce((best, c) => (!best || c.start > best.start ? c : best), null);
}
/** The text with the date phrase removed, which is what becomes the title. */
export function stripDatePhrase(text, parsed) {
    if (!parsed)
        return text.trim();
    return `${text.slice(0, parsed.start)}${text.slice(parsed.end)}`.replace(/\s{2,}/g, ' ').trim();
}
//# sourceMappingURL=dates.js.map